import { computed, inject, Injectable, signal } from '@angular/core';
import {
  GetCommissionAdiEligibiliteGQL,
  GetRenouvellementEligibiliteGQL,
  SaveRenouvellementComAdiReponseGQL,
  SaveRenouvellementReponseGQL,
} from '@graphql/generated';
import { lastValueFrom } from 'rxjs';
import { CommissionAdiType } from '@lib/models/commission-adi.model';
import { CommissionAdiTypeMapper } from '@lib/mappers/graphql/commission-adi-type-mapper';

@Injectable({
  providedIn: 'root',
})
export class RenouvellementService {
  private readonly getRenouvellementEligibiliteGql = inject(
    GetRenouvellementEligibiliteGQL
  );
  private readonly getCommissionAdiEligibiliteGql = inject(
    GetCommissionAdiEligibiliteGQL
  );
  private readonly saveRenouvellementReponseGql = inject(SaveRenouvellementReponseGQL);
  private readonly saveRenouvellementComAdiReponseGql = inject(
    SaveRenouvellementComAdiReponseGQL
  );

  private readonly _isEligibleRenouvellement = signal<boolean>(false);
  private readonly _isEligibleCommissionAdi = signal<boolean>(false);
  private readonly _actuelCommissionAdi = signal<CommissionAdiType>('precompte');

  public readonly isEligibleRenouvellement = computed(() =>
    this._isEligibleRenouvellement()
  );
  public readonly isEligibleCommissionAdi = computed(() =>
    this._isEligibleCommissionAdi()
  );
  public readonly actuelCommissionAdi = computed(() => this._actuelCommissionAdi());

  public async getIsEligibleRenouvellement(): Promise<boolean> {
    const response = await lastValueFrom(this.getRenouvellementEligibiliteGql.fetch());
    const result = response.data!.consultantRenouvellementEligibilite;

    this._isEligibleRenouvellement.set(result);
    return result;
  }

  public async getIsEligibleCommissionAdi(): Promise<boolean> {
    const response = await lastValueFrom(this.getCommissionAdiEligibiliteGql.fetch());
    const result = response.data!.consultantRenouvellementComAdiEligibilite;
    const isEligibleComAdi = result != null;
    const actualComAdi: CommissionAdiType = isEligibleComAdi
      ? CommissionAdiTypeMapper.fromGraphQl(result.actuelCommissionAdiTypeAsEnum)
      : 'precompte';

    this._isEligibleCommissionAdi.set(isEligibleComAdi);
    this._actuelCommissionAdi.set(actualComAdi);
    return isEligibleComAdi;
  }

  public async acceptRenouvellement(): Promise<void> {
    await lastValueFrom(
      this.saveRenouvellementReponseGql.mutate({
        variables: {
          renouvellement: true,
        },
      })
    );

    this._isEligibleRenouvellement.set(false);
  }

  public async refuseRenouvellement(): Promise<void> {
    await lastValueFrom(
      this.saveRenouvellementReponseGql.mutate({ variables: { renouvellement: false } })
    );

    this._isEligibleRenouvellement.set(false);
  }

  public async saveRenouvellementComAdi(
    commissionAdiType: CommissionAdiType
  ): Promise<void> {
    await lastValueFrom(
      this.saveRenouvellementComAdiReponseGql.mutate({
        variables: {
          commissionAdiType: CommissionAdiTypeMapper.toGraphql(commissionAdiType)!,
        },
      })
    );

    this._isEligibleCommissionAdi.set(false);
  }
}
